/**
 * The demo patient — Gloria Alejandra Delrío.
 *
 * Synthetic chart from the dev tenant (confirmed by the product owner).
 *
 * EVERYTHING HERE IS REAL, captured from the live dev system. Nothing is
 * authored. Where the system did not give us something — labs, allergies,
 * immunizations, encounter history — it is absent rather than invented, and
 * the surfaces that would have shown it render their genuine empty states.
 *
 * The two exceptions are labelled inline: the birth date (derived from the
 * age the captured answers state) and the session id (a local handle, not
 * clinical data).
 */

import { CHART_CONDITIONS } from './chart.generated';

/** The demo's fixed "today" — the day the chart was captured. */
export const DEMO_TODAY = new Date('2026-08-22T00:00:00Z');

/** A local handle for the demo's SMART session. Not clinical data. */
export const DEMO_SESSION_ID = 'demo-session-001';

export const DEMO_TENANT_ID = 'dev-tenant';
export const DEMO_PATIENT_FHIR_ID = '7b802e8a-8dd6-ade1-d688-ee5adc74fc0d';
export const DEMO_MRN = '7b802e8a-8dd6-ade1-d688-ee5adc74fc0d';
export const DEMO_PATIENT_NAME = 'Gloria Alejandra Delrío';

/** The history window the captured session ran under. */
export const DEMO_HISTORY_WINDOW = '10y';

/**
 * FHIR Patient resource, as fetchSmartPatient expects (types/smart.ts).
 *
 * Carries only what the captured answers establish: the name, the identifier
 * and the sex ("Sex: Female" in the demographic considerations). The birth
 * date is DERIVED from the stated "Age: 80 years old" — the day and month are
 * not in anything we captured, so treat that field as approximate. Address,
 * telephone and managing organization are deliberately omitted rather than
 * invented; the patient drawer shows them as absent.
 */
export const demoPatientResource = {
  resourceType: 'Patient' as const,
  id: DEMO_PATIENT_FHIR_ID,
  active: true,
  name: [{ given: ['Gloria', 'Alejandra'], family: 'Delrío', text: DEMO_PATIENT_NAME, use: 'official' }],
  gender: 'female',
  birthDate: '1946-01-01',
  identifier: [{ system: 'urn:demo:mrn', value: DEMO_MRN, type: { text: 'MRN' } }],
};

/**
 * REAL: the 106 dated diastolic readings, generated from the captured BP
 * answer's own table (see chart.generated.ts). The answer states the count and
 * both endpoints, so the plotted series and the prose cannot drift apart.
 */
export { DIASTOLIC_SERIES as diastolicSeries } from './chart.generated';

/** REAL: the 24 active conditions, exactly as /conditions returned them. */
export const conditions = CHART_CONDITIONS;

/**
 * REAL: the medications the captured answers name, with the RxCUIs they link
 * to. The interaction warnings in the medications answer are about exactly
 * these drugs, so the list has to match or the demo contradicts itself.
 *
 * Prescription dates and sigs are NOT included — the answers name the drugs
 * and their strengths, not when they were ordered, and inventing an order
 * history would put fabricated dates on a timeline.
 */
export const medications = [
  { name: 'Lisinopril 10 MG Oral Tablet', rxnorm: '314076' },
  { name: 'Hydrochlorothiazide 25 MG Oral Tablet', rxnorm: '310798' },
  { name: 'Simvastatin 20 MG Oral Tablet', rxnorm: '312961' },
  { name: 'amLODIPine 2.5 MG Oral Tablet', rxnorm: '308136' },
  { name: '24 HR metoprolol succinate 100 MG Extended Release Oral Tablet', rxnorm: '866412' },
  { name: 'Nitroglycerin 0.4 MG/ACTUAT Mucosal Spray', rxnorm: '705129' },
  { name: 'Levothyroxine Sodium 0.075 MG Oral Tablet', rxnorm: '966222' },
  {
    name: 'insulin isophane, human 70 UNT/ML / insulin, regular, human 30 UNT/ML Injectable Suspension Humulin',
    rxnorm: '106892',
  },
];

/** Every medication named is a current one — the answers discuss them as active. */
export const activeMedications = medications;

/**
 * Resource counts for the patient drawer. Only the two resource types we
 * actually hold are counted; claiming a lab or encounter total we never
 * received would be the same invention in tally form.
 */
export const resourceCounts = [
  { resourceType: 'Condition', count: conditions.length },
  { resourceType: 'MedicationRequest', count: medications.length },
];

/**
 * SmartClinicalPreview for the patient drawer. The arrays we have no captured
 * data for stay empty, so the drawer reports them as absent rather than
 * showing something that was never in the chart.
 */
export const demoClinicalPreview = {
  strategy: 'captured-from-dev',
  counts: resourceCounts,
  conditions: conditions.map((c, i) => ({
    id: `cond-${i}`,
    title: c.title,
    subtitle: c.code,
    date: c.onset,
    status: c.status,
    category: 'problem-list-item',
  })),
  medications: medications.map((m, i) => ({
    id: `med-${i}`,
    title: m.name,
    subtitle: `RxNorm ${m.rxnorm}`,
  })),
  observations: [],
  encounters: [],
  allergies: [],
  procedures: [],
  reports: [],
  documents: [],
  carePlans: [],
  immunizations: [],
};
